import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {QrCodeData} from "types/QrCode";
import QrCodeForm from "pages/QrCode/Form";

function MutateQrCode() {
    const {id} = useParams()
    const {data: qrCode, isLoading} = useQuery(
        ['qr-codes', {id}],
        () => ngage.qrCodes.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: QrCodeData) => ngage.qrCodes.store(data))
    const update = useMutation((data: QrCodeData) => ngage.qrCodes.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: QrCodeData, {setSubmitting}: FormikHelpers<QrCodeData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('qr-codes.all')

        setSubmitting(false)
        navigate('/qr-codes')
    }

    // Handle store mutation
    async function handleStore(values: QrCodeData) {
        try {
            await store.mutateAsync(values)
            notify("success", "QR Code Created", `${values.name} has been created`)
        } catch (error) {
            notify("error", "QR Code Not Created", `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: QrCodeData) {
        try {
            await update.mutateAsync(values)
            notify("success", "QR Code Updated", `${values.name} has been updated`)
            await invalidate(['qr-codes', {id}])
        } catch (error) {
            notify("error", "QR Code Not Updated", `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Operations': '/', 'QR Codes': '/qr-codes', 'New QR Code': '/qr-codes/new'}
    const editBreadcrumbs = {'Operations': '/', 'QR Codes': '/qr-codes', 'Edit QR Code': `/qr-codes/${id}/edit`}

    return (
        <Page title={id ? "Edit QR Code" : "New QR Code"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <QrCodeForm qrCode={qrCode} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateQrCode;